import { useId } from 'react';
import styles from './objects.module.css';

const CX = 100;
const CY = 78;
const TICKS = 40;

/** The angle the dial turns to for a digit: ten digits spread round it, the typed one on top. */
export function dialAngle(digit: number | null, turns = 0): number {
  return -((digit ?? 0) * 36) - turns * 360;
}

/**
 * The safe: a steel door with a dial that turns to each digit as it is typed, and a panel of
 * lamps for the attempts left and for the last answer (a green lamp for every click, an amber
 * one for every hum).
 */
export function SafeObject({
  typed,
  left,
  clicks = 0,
  hums = 0,
  jammed = false,
  turns = 0,
}: {
  /** The digit typed last, or null before any. */
  typed: number | null;
  left: number;
  clicks?: number;
  hums?: number;
  jammed?: boolean;
  /** Full turns so far, so that a new attempt spins the dial. */
  turns?: number;
}) {
  const id = useId();
  const steel = `${id}-steel`;
  const face = `${id}-face`;
  return (
    <div className={styles['hero']}>
      <svg className={styles['svg']} viewBox="0 0 300 160" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={steel} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#59627a" />
            <stop offset="0.5" stopColor="#3b4256" />
            <stop offset="1" stopColor="#262b3b" />
          </linearGradient>
          <radialGradient id={face} cx="0.4" cy="0.35" r="0.8">
            <stop offset="0" stopColor="#4b5368" />
            <stop offset="1" stopColor="#1c2030" />
          </radialGradient>
        </defs>
        <g className={jammed ? styles['jam'] : undefined}>
          <rect
            x="8"
            y="8"
            width="284"
            height="144"
            rx="12"
            fill={`url(#${steel})`}
            stroke="#7a8496"
            strokeWidth="2"
          />
          {[
            [18, 18],
            [282, 18],
            [18, 142],
            [282, 142],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="3" fill="#8a929c" stroke="#262b3b" />
          ))}
          <circle cx={CX} cy={CY} r="60" fill="#14182a" stroke="#8a929c" strokeWidth="3" />
          <circle cx={CX} cy={CY} r="54" fill={`url(#${face})`} />
          <g
            className={styles['dial']}
            style={{
              transform: `rotate(${dialAngle(typed, turns)}deg)`,
              transformOrigin: `${CX}px ${CY}px`,
            }}
          >
            {Array.from({ length: TICKS }, (_, i) => {
              const a = (i / TICKS) * Math.PI * 2 - Math.PI / 2;
              const major = i % 4 === 0;
              const r1 = major ? 40 : 44;
              return (
                <line
                  key={i}
                  x1={CX + Math.cos(a) * r1}
                  y1={CY + Math.sin(a) * r1}
                  x2={CX + Math.cos(a) * 50}
                  y2={CY + Math.sin(a) * 50}
                  stroke="var(--c-brass-light)"
                  strokeWidth={major ? 2 : 1}
                />
              );
            })}
            {Array.from({ length: 10 }, (_, d) => {
              const a = (d / 10) * Math.PI * 2 - Math.PI / 2;
              return (
                <text
                  key={d}
                  x={CX + Math.cos(a) * 31}
                  y={CY + Math.sin(a) * 31 + 4}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="700"
                  fill="var(--c-lamp-text)"
                  transform={`rotate(${d * 36} ${CX + Math.cos(a) * 31} ${CY + Math.sin(a) * 31})`}
                >
                  {d}
                </text>
              );
            })}
            <circle cx={CX} cy={CY} r="13" fill="#2a303a" stroke="var(--c-brass)" strokeWidth="2" />
            <rect x={CX - 2} y={CY - 13} width="4" height="12" rx="2" fill="var(--c-brass-light)" />
          </g>
          <polygon
            points={`${CX - 6},${CY - 66} ${CX + 6},${CY - 66} ${CX},${CY - 57}`}
            fill="var(--c-neon-amber)"
            stroke="var(--c-ink-950)"
          />
          <rect x="190" y="26" width="86" height="108" rx="8" fill="#171b2a" stroke="#5a6476" />
          {Array.from({ length: 12 }, (_, i) => (
            <circle
              key={i}
              className={styles['lamp']}
              cx={206 + (i % 4) * 22}
              cy={44 + Math.floor(i / 4) * 14}
              r="4"
              fill={i < left ? 'var(--c-neon-cyan)' : '#2b3147'}
              opacity={i < left ? 1 : 0.7}
            />
          ))}
          {Array.from({ length: 6 }, (_, i) => (
            <circle
              key={`c${i}`}
              className={styles['lamp']}
              cx={206 + (i % 3) * 30}
              cy={92 + Math.floor(i / 3) * 14}
              r="6"
              fill={i < clicks ? 'var(--c-neon-green)' : '#23382e'}
            />
          ))}
          {Array.from({ length: 3 }, (_, i) => (
            <rect
              key={`m${i}`}
              className={styles['lamp']}
              x={198 + i * 26}
              y="118"
              width="20"
              height="6"
              rx="3"
              fill={i < hums ? 'var(--c-neon-amber)' : '#3a3322'}
            />
          ))}
        </g>
      </svg>
    </div>
  );
}
